import React from 'react'
import { pantryColors, pantryRadii } from '@pantry/ui'
import { AccordionCard } from '../components/AccordionCard'
import { SpecFieldsEditor, type SpecFieldSlot } from '../components/SpecFieldsEditor'
import { specColors } from '../../constants'
import { PAGE_SCORE_TARGET, revealCustomVariants } from '../../pageScore'
import { useProductDetailContext } from '../../hooks/useProductDetailContext'
import { usePantryIsUnlinkPos } from '../../../../providers/PantryProvider'
import {
  customVariants,
  defaultSpecSetFromDraft,
  diffAgainstDefault,
  variantLabel,
  type VariantValueDiff,
} from '../../utils/variationSpecs'
import type { HiddenSpecField, ProductDraft, SpecSet, VariationGroupItem } from '../../../../types/product'

interface SpecificationsSectionProps {
  draft: ProductDraft
  onPatch: (patch: Partial<ProductDraft>) => void
  onSetHiddenSpecFields: (fields: HiddenSpecField[]) => void
  // Opens the per-variant "Edit specifications" dialog (shared with Variations).
  onEditVariationSpecs: (variationId: string) => void
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: pantryColors.gray[600],
  marginBottom: 6,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 4,
  lineHeight: 1.4,
}

const posTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 4,
  background: pantryColors.amber[50],
  color: pantryColors.amber[700],
  border: `1px solid ${pantryColors.amber[200] ?? '#fde68a'}`,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
}

const lockedInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: `1px solid ${pantryColors.amber[200] ?? '#fde68a'}`,
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  color: pantryColors.gray[500],
  background: '#FAFAF8',
  fontFamily: 'inherit',
  cursor: 'default',
  outline: 'none',
}

const hintStyle: React.CSSProperties = { fontSize: 11, color: pantryColors.gray[400], marginTop: 5 }

const awareBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 22,
  padding: '0 9px',
  border: `1px solid ${pantryColors.purple[200]}`,
  borderRadius: pantryRadii.pill,
  background: pantryColors.purple[50],
  color: pantryColors.purple[700],
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  flexShrink: 0,
  whiteSpace: 'nowrap',
}

const awareDotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: pantryColors.purple[700],
  display: 'inline-block',
  flexShrink: 0,
}

const bannerStyle = (muted: boolean): React.CSSProperties => ({
  display: 'flex',
  gap: 10,
  padding: '10px 12px',
  marginBottom: 16,
  border: `1px solid ${muted ? pantryColors.gray[200] : pantryColors.purple[200]}`,
  borderRadius: 10,
  background: muted ? pantryColors.gray[50] : specColors.purpleSoft,
  fontSize: 12,
  color: muted ? pantryColors.gray[500] : specColors.bannerText,
  lineHeight: 1.5,
})

const linkButtonStyle = (danger = false): React.CSSProperties => ({
  fontSize: 12,
  fontWeight: 500,
  color: danger ? pantryColors.red[700] : pantryColors.brand.primary,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: 0,
})

const diffStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  marginTop: 6,
  fontSize: 11,
  color: specColors.diffText,
  lineHeight: 1.6,
}

const diffCannabinoidStyle: React.CSSProperties = { ...diffStyle, margin: '-3px 0 6px 14px' }

const diffExtraStyle: React.CSSProperties = {
  ...diffStyle,
  margin: '14px 0 0',
  padding: '8px 10px',
  border: `1px dashed ${specColors.purpleBorder}`,
  borderRadius: 8,
  background: specColors.purpleSoft,
}

const variantLinkStyle: React.CSSProperties = {
  border: 'none',
  background: pantryColors.purple[50],
  color: pantryColors.purple[700],
  font: 'inherit',
  fontWeight: 700,
  padding: '0 6px',
  borderRadius: 4,
  cursor: 'pointer',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// The API can send weights as numeric strings (e.g. non-pantry POS products).
function toWeight(val: number | string | null | undefined): number | null {
  if (val == null || val === '') return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

function fmtWeight(val: number | string | null | undefined): string {
  const n = toWeight(val)
  return n == null ? '' : `${n.toFixed(2)} g`
}

interface WeightSummary {
  value: string
  // "Varies by variant · 28g: 28.00 g · 3.5g: 3.50 g" when the variants differ.
  hint: string | null
}

// Net Weight / Cannabis Equiv. come from POS per variant: one value when they
// all match, otherwise the `min – max g` range plus a per-variant hint.
export function summarizeWeight(
  items: VariationGroupItem[],
  pick: (item: VariationGroupItem) => number | string | null | undefined,
): WeightSummary {
  const values = items.map((item) => toWeight(pick(item))).filter((v): v is number => v != null)
  if (values.length === 0) return { value: '', hint: null }
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) return { value: fmtWeight(min), hint: null }
  return {
    value: `${min.toFixed(2)} – ${max.toFixed(2)} g`,
    hint: `Varies by variant · ${items.map((item) => `${variantLabel(item)}: ${fmtWeight(pick(item)) || '—'}`).join(' · ')}`,
  }
}

const PosIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 9, height: 9 }}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
)

const InfoIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1, color }}
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
)

interface PosFieldProps {
  label: string
  ariaLabel: string
  summary: WeightSummary
  loading: boolean
}

const PosField: React.FC<PosFieldProps> = ({ label, ariaLabel, summary, loading }) => (
  <div style={{ minWidth: 0 }}>
    <div style={labelStyle}>
      {label}
      <span style={posTagStyle}><PosIcon /> POS</span>
    </div>
    <input
      type="text"
      readOnly
      value={loading ? '…' : summary.value}
      placeholder="—"
      style={lockedInputStyle}
      aria-label={ariaLabel}
    />
    {!loading && summary.hint && <div style={hintStyle}>{summary.hint}</div>}
  </div>
)

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  color: pantryColors.gray[800],
  background: pantryColors.white,
  fontFamily: 'inherit',
  outline: 'none',
}

function parseWeightInput(raw: string): number | null {
  if (raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

// Unlink-POS: weight fields aren't POS-synced, so they are editable product-level values.
const EditableWeightField: React.FC<{
  label: string
  ariaLabel: string
  value: number | null | undefined
  onChange: (value: number | null) => void
}> = ({ label, ariaLabel, value, onChange }) => (
  <div style={{ minWidth: 0 }}>
    <div style={labelStyle}>{label}</div>
    <input
      type="number"
      min={0}
      step="any"
      value={value ?? ''}
      onChange={(e) => onChange(parseWeightInput(e.target.value))}
      placeholder="e.g. 3.5"
      style={inputStyle}
      aria-label={ariaLabel}
    />
  </div>
)

// One ◆ line: "◆ [prefix] part · part · …".
const DiffLine: React.FC<{ style?: React.CSSProperties; prefix?: string; parts: React.ReactNode[] }> = ({
  style = diffStyle,
  prefix,
  parts,
}) => (
  <div style={style} data-spec-diff>
    <span style={{ color: specColors.diffMark, fontSize: 9 }} aria-hidden>◆</span>
    {prefix && <span>{prefix}</span>}
    {parts.map((part, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span style={{ color: specColors.diffSeparator }}>·</span>}
        {part}
      </React.Fragment>
    ))}
  </div>
)

// ─── SpecificationsSection ────────────────────────────────────────────────────

export const SpecificationsSection: React.FC<SpecificationsSectionProps> = ({
  draft,
  onPatch,
  onSetHiddenSpecFields,
  onEditVariationSpecs,
}) => {
  const { variationGroup, isLoadingVariationGroup, editor } = useProductDetailContext()
  // In unlink-POS state these fields aren't POS-synced, so they become editable
  // (product-level values applied to every variation at save time).
  const isUnlinkPos = usePantryIsUnlinkPos()

  const icon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 15, height: 15 }}>
      <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )

  const hidden = draft.hiddenSpecFields ?? []
  const defaultSpecs = defaultSpecSetFromDraft(draft)
  const total = variationGroup.length
  const multiVariant = total > 1
  const customs = multiVariant ? customVariants(draft, variationGroup) : []
  const diff = customs.length > 0 ? diffAgainstDefault(defaultSpecs, customs, hidden) : null

  const netWeight = summarizeWeight(variationGroup, (v) => v.weight)
  const cannabisEq = summarizeWeight(variationGroup, (v) => v.cannabis_weight)
  const defaultLabel = variationGroup[0] ? variantLabel(variationGroup[0]) : ''
  const labelOf = (id: string) => {
    const item = variationGroup.find((v) => v._id === id)
    return item ? variantLabel(item) : id
  }

  const variantLink = (id: string) => (
    <button
      type="button"
      style={variantLinkStyle}
      title={`Open ${labelOf(id)} specifications`}
      onClick={() => onEditVariationSpecs(id)}
    >
      {labelOf(id)}
    </button>
  )

  const valueParts = (diffs: VariantValueDiff[]) =>
    diffs.map((d) => (
      <span key={d.itemId}>
        {variantLink(d.itemId)} {d.text || 'not set'}
      </span>
    ))

  const renderBelow = (slot: SpecFieldSlot): React.ReactNode => {
    if (!diff) return null
    if (slot.kind === 'cannabinoid') {
      const d = diff.cannabinoids[slot.name]
      return d.length > 0 ? <DiffLine style={diffCannabinoidStyle} parts={valueParts(d)} /> : null
    }
    if (slot.kind === 'volume') {
      return diff.volume.length > 0 ? <DiffLine parts={valueParts(diff.volume)} /> : null
    }
    if (slot.kind === 'list') {
      const d = diff[slot.field]
      if (d.length === 0) return null
      return (
        <DiffLine
          parts={d.map(({ itemId, added, removed }) => (
            <span key={itemId} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              {variantLink(itemId)}
              {added.map((x) => (
                <span key={`+${x}`} style={{ color: specColors.diffAdded, fontWeight: 500 }}>+ {x}</span>
              ))}
              {removed.map((x) => (
                <span key={`-${x}`} style={{ color: specColors.diffRemoved, textDecoration: 'line-through' }}>{x}</span>
              ))}
            </span>
          ))}
        />
      )
    }
    if (slot.kind === 'custom') {
      const d = diff.customSpecs[slot.index] ?? []
      return d.length > 0 ? <DiffLine parts={valueParts(d)} /> : null
    }
    if (diff.onlyOnVariants.length === 0) return null
    return (
      <DiffLine
        style={diffExtraStyle}
        prefix="Only on variants:"
        parts={diff.onlyOnVariants.map((x, i) => (
          <span key={`${x.itemId}-${i}`}>
            {variantLink(x.itemId)} {x.key}: {x.text || '—'}
          </span>
        ))}
      />
    )
  }

  const awareBadge = customs.length > 0 && (
    <button
      type="button"
      style={awareBadgeStyle}
      title="Show customized variants"
      onClick={(e) => {
        e.stopPropagation()
        revealCustomVariants()
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span style={awareDotStyle} />
      {customs.length} of {total} variants customized
    </button>
  )

  const banner = multiVariant && (
    customs.length === 0 ? (
      <div style={bannerStyle(true)} data-testid="spec-default-banner">
        <InfoIcon color={pantryColors.gray[400]} />
        <span>
          Specifications of the default variant <b>{defaultLabel}</b> — applied to all {total} variants.
        </span>
      </div>
    ) : (
      <div style={bannerStyle(false)} data-testid="spec-default-banner">
        <InfoIcon color={pantryColors.purple[700]} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div>
            Specifications of the default variant <b>{defaultLabel}</b> — used by {total - customs.length} of {total}{' '}
            variants. Changes here won’t affect variants with custom specifications.
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${specColors.purpleHover}`,
            }}
          >
            {customs.map(({ itemId }) => (
              <div key={itemId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={awareDotStyle} />
                <span>
                  <b>{labelOf(itemId)}</b> uses custom specifications
                </span>
                <button
                  type="button"
                  style={{ ...linkButtonStyle(), marginLeft: 'auto' }}
                  onClick={() => onEditVariationSpecs(itemId)}
                >
                  View
                </button>
                <button
                  type="button"
                  style={linkButtonStyle(true)}
                  onClick={() => editor.resetVariationToDefault(itemId)}
                >
                  Reset to default
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  )

  const hide = (field: HiddenSpecField) => {
    if (!hidden.includes(field)) onSetHiddenSpecFields([...hidden, field])
  }
  const show = (field: HiddenSpecField) => onSetHiddenSpecFields(hidden.filter((f) => f !== field))

  return (
    <AccordionCard
      id={PAGE_SCORE_TARGET.specs}
      title="Specifications"
      subtitle="THC, CBD, terpenes, effects, and product details"
      icon={icon}
      iconBg={pantryColors.brand.light}
      iconColor={pantryColors.brand.primary}
      defaultOpen={false}
      headerRight={awareBadge || undefined}
    >
      {banner}

      <SpecFieldsEditor
        value={defaultSpecs}
        onChange={(patch: Partial<SpecSet>) => onPatch(patch)}
        hidden={hidden}
        onHide={hide}
        onShow={show}
        showHideControls
        addLabel="Add field"
        leadingFields={
          isUnlinkPos ? (
            <>
              <EditableWeightField
                label="Net Weight"
                ariaLabel="Net Weight (grams)"
                value={draft.weight}
                onChange={(weight) => onPatch({ weight })}
              />
              <EditableWeightField
                label="Cannabis Equiv."
                ariaLabel="Cannabis Equivalent (grams)"
                value={draft.cannabisWeight}
                onChange={(cannabisWeight) => onPatch({ cannabisWeight })}
              />
            </>
          ) : (
            <>
              <PosField
                label="Net Weight"
                ariaLabel="Net Weight (read-only from POS)"
                summary={netWeight}
                loading={isLoadingVariationGroup}
              />
              <PosField
                label="Cannabis Equiv."
                ariaLabel="Cannabis Equivalent (read-only from POS)"
                summary={cannabisEq}
                loading={isLoadingVariationGroup}
              />
            </>
          )
        }
        renderBelow={renderBelow}
      />
    </AccordionCard>
  )
}
